import {
  TBitValue,
  TReactSetState,
  TUseTranslationTfn,
} from "../../data/AppType";
import * as Yup from "yup";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";

import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  CREATE_HSN_SAC_CODE,
  GET_HSN_SAC_CODE_BY_ID,
  UPDATE_HSN_SAC_CODE,
} from "../../services/Endpoints";

export interface IHsnSacCodeForm {
  id?: string;
  hsn_sac_code: string;
  chapter: string;
  description: string;
  rate_percentage: number[] | null[];
  effective_from?: string;
  end_date?: Date;
  is_active: TBitValue;
}

export const HSN_SAC_CODE_FIELD_INITIAL_VALUES = {
  hsn_sac_code: "",
  chapter: "",
  description: "",
  rate_percentage: [null],
  effective_from: undefined,
  is_active: BIT_VALUE.One,
};
export const hsnSacCodeFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    hsn_sac_code: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("hsn_sac_code")],
        ]),
      )
      .max(
        10,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("hsn_sac_code")],
          ["max", "10"],
        ]),
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("hsn_sac_code")],
          ["min", "1"],
        ]),
      ),
    description: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("description")],
        ]),
      )
      .max(
        300,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("description")],
          ["max", "300"],
        ]),
      )
      .min(
        10,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("description")],
          ["min", "10"],
        ]),
      ),
    chapter: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("chapter")],
        ]),
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("chapter")],
          ["max", "50"],
        ]),
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("chapter")],
          ["min", "1"],
        ]),
      ),
    rate_percentage: Yup.array().of(
      Yup.number()
        .typeError(
          prepareMessageFromParams(t("error_message_required"), [
            ["fieldName", t("rate_percentage")],
          ]),
        )
        .required(
          prepareMessageFromParams(t("error_message_required"), [
            ["fieldName", t("rate_percentage")],
          ]),
        )
        .min(
          0.001,
          prepareMessageFromParams(t("error_message_min_length"), [
            ["fieldName", t("rate_percentage")],
            ["min", "0.001"],
          ]),
        )
        .max(
          100,
          prepareMessageFromParams(t("error_message_max_length"), [
            ["fieldName", t("rate_percentage")],
            ["max", "100"],
          ]),
        ),
    ),
    effective_from: Yup.date().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("effective_from")],
      ]),
    ),
  });

export const handleSubmitHsnSacCode = async (
  values: IHsnSacCodeForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void,
) => {
  setLoading(true);
  try {
    const payload = {
      ...values,
      rate_percentage: values.rate_percentage.filter(
        (rate): rate is number => rate !== null,
      ),
    };
    let resultHsnSacCode;
    if (id) {
      resultHsnSacCode = await UPDATE_HSN_SAC_CODE(payload, id);
    } else {
      resultHsnSacCode = await CREATE_HSN_SAC_CODE(payload);
    }
    if (
      resultHsnSacCode &&
      resultHsnSacCode.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      sweetAlertSuccess({
        message: resultHsnSacCode.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: resultHsnSacCode?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitHsnSacCode");
  } finally {
    setLoading(false);
  }
};

export const fetchHsnSacCodeById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setHsnSacCode: TReactSetState<IHsnSacCodeForm | undefined>,
) => {
  try {
    const hsnSacCodeData = await GET_HSN_SAC_CODE_BY_ID(id);
    if (hsnSacCodeData && hsnSacCodeData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setHsnSacCode({
        ...hsnSacCodeData.data,
        end_date: hsnSacCodeData.data.end_date
          ? hsnSacCodeData.data.end_date
          : undefined,
      });
    } else {
      handleClose(false);
      sweetAlertError({
        message: hsnSacCodeData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchHsnSacCodeById");
  }
};
